import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, NavLink, Navigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, ClipboardCheck, BarChart3, Sparkles, PhoneCall,
  ShieldCheck, LogOut, Menu, X, ArrowRight, Command, Globe2, Bot, Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SECTIONS = [
  {
    label: "Oversight",
    items: [
      { name: "Overview",  icon: LayoutDashboard, path: "overview" },
      { name: "Analytics", icon: BarChart3,       path: "analytics" },
    ],
  },
  {
    label: "Knowledge operations",
    items: [
      { name: "Content Management", icon: FolderKanban,    path: "content" },
      { name: "Approvals",          icon: ClipboardCheck,  path: "approvals", badgeKey: "pending" },
      { name: "What's New",         icon: Sparkles,        path: "whats-new" },
      { name: "Support Contacts",   icon: PhoneCall,       path: "support" },
    ],
  },
  {
    label: "Governance",
    items: [{ name: "Administration", icon: ShieldCheck, path: "admin", adminOnly: true }],
  },
];

export default function ConsoleShell() {
  const { user, logout, setActiveProgram } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [scope, setScope] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const isManager = user?.is_admin || user?.role_category === "rc_approver";

  useEffect(() => {
    if (!isManager) return;
    const refresh = () => {
      api.get("/documents/pending").then((r) => setPending(r.data.length)).catch(() => {});
      api.get("/programs").then((r) => setScope(r.data.filter((p) => p.managed).length)).catch(() => {});
    };
    refresh();
    window.addEventListener("approvals-changed", refresh);
    return () => window.removeEventListener("approvals-changed", refresh);
  }, [isManager, location.pathname]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!isManager) return <Navigate to="/app/dashboard" replace />;

  const badges = { pending };
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const enterWorkspace = () => { setActiveProgram(null); navigate("/"); };

  const currentPath = location.pathname.split("/").pop();
  const current = SECTIONS.flatMap((s) => s.items).find((i) => i.path === currentPath);

  const SidebarInner = () => (
    <>
      <div className="px-4 py-5 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0 shadow-[0_8px_20px_-8px_hsl(224_54%_21%/0.5)]">
            <Bot className="w-5 h-5 text-primary-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-[17px] leading-tight text-foreground truncate">Ask <span className="italic text-primary">Z-Method</span></p>
            <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-mono uppercase tracking-[0.22em] text-primary bg-accent/40 border border-accent px-1.5 py-0.5 rounded">
              <Command className="w-2.5 h-2.5" /> Console
            </span>
          </div>
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="mt-4 rounded-xl bg-secondary/40 border border-border px-3 py-2.5 flex items-start gap-2">
          <Globe2 className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Cross-program scope</p>
            <p className="text-sm font-semibold text-foreground mt-0.5">
              {user?.is_admin ? "All programs" : `${scope ?? "…"} program${scope === 1 ? "" : "s"} you own`}
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto kp-scroll py-4 px-2.5 space-y-5 kp-console-rail">
        {SECTIONS.map((section) => {
          const items = section.items.filter((i) => !i.adminOnly || user?.is_admin);
          if (!items.length) return null;
          return (
            <div key={section.label}>
              <p className="px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70 font-semibold">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink key={item.path} to={item.path} data-testid={`console-nav-${item.path}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-[0_6px_18px_-8px_hsl(224_54%_21%/0.55)]"
                          : "text-foreground/70 hover:bg-secondary/60 hover:text-foreground"
                      }`
                    }>
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span className="truncate flex-1">{item.name}</span>
                    {item.badgeKey && badges[item.badgeKey] > 0 && (
                      <span className="text-[10px] font-bold rounded-full bg-destructive text-destructive-foreground px-1.5 py-0.5 min-w-[18px] text-center" data-testid="approvals-badge">
                        {badges[item.badgeKey]}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <button onClick={enterWorkspace} data-testid="go-program-workspace"
        className="m-2.5 rounded-xl border border-border bg-accent/25 hover:bg-accent/40 px-3 py-2.5 text-left transition group">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Switch to</p>
        <p className="text-sm font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
          Program Workspace
          <ArrowRight className="w-3.5 h-3.5 ml-auto opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
        </p>
      </button>
    </>
  );

  return (
    <div className="min-h-screen kp-paper flex">
      <aside className="hidden lg:flex flex-col w-72 bg-card/70 backdrop-blur border-r border-border shrink-0">
        <SidebarInner />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden kp-fade">
          <div className="absolute inset-0 bg-foreground/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-card flex flex-col shadow-2xl kp-in"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-background/85 backdrop-blur-md border-b border-border flex items-center gap-3 px-5 sticky top-0 z-30">
          <button className="lg:hidden text-foreground" onClick={() => setMobileOpen(true)} data-testid="console-mobile-menu">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden md:flex items-center text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground gap-1.5 min-w-0">
            <Command className="w-3 h-3" />
            <span className="truncate">Console</span>
            <span className="opacity-40">/</span>
            <span className="text-foreground font-semibold truncate">{current?.name || "Overview"}</span>
          </div>

          <button onClick={() => setSearchOpen(true)} data-testid="console-search-trigger"
            className="ml-auto md:ml-6 flex-1 min-w-0 max-w-md flex items-center gap-2 rounded-full border border-border bg-card/70 px-4 py-2 text-sm text-muted-foreground hover:border-primary/40 hover:bg-card transition-all">
            <Search className="w-4 h-4 shrink-0" />
            <span className="truncate min-w-0 font-normal">Search documents, users, programs…</span>
            <kbd className="ml-auto hidden sm:inline text-[10px] font-mono bg-background border border-border rounded px-1.5 py-0.5">⌘K</kbd>
          </button>

          <button onClick={enterWorkspace} data-testid="topbar-program-workspace"
            className="hidden xl:inline-flex text-xs font-medium rounded-full border border-primary/25 bg-accent/25 text-primary px-3.5 py-1.5 hover:bg-primary hover:text-primary-foreground hover:border-primary transition items-center gap-1.5">
            <ArrowRight className="w-3.5 h-3.5" /> Program Workspace
          </button>

          <NotificationBell to="/manage/whats-new" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full hover:bg-secondary/60 pl-1 pr-2 py-1 transition" data-testid="user-menu-trigger">
                <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center uppercase">{initials}</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-xs font-semibold text-foreground">{user?.name}</span>
                  <span className="block text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{user?.is_admin ? "Administrator" : "Program Manager"}</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="font-semibold">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-normal">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={enterWorkspace} data-testid="menu-program-workspace">
                <ArrowRight className="w-4 h-4 mr-2" /> Program Workspace
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto kp-scroll p-5 md:p-10 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} activeProgram={null} />
    </div>
  );
}
